/**
 * src/components/spatial/SpatialNearbyPanel.tsx
 *
 * "Something is happening here, now."
 *
 * The proximity hook and the broadcast card were both built and neither
 * was mounted anywhere, so an object could be live, in range and audible
 * and the map would say nothing about it. This is the small amount of
 * chrome that connects them: what is within earshot of where the listener
 * is standing, and a way in.
 *
 * Reading only. `track` is left off, so opening a map does not wake every
 * building the listener walks past — a proximity trigger is a side effect,
 * and starting a broadcast because somebody scrolled is not a feature.
 * The listener's own position is what the host app already knows; nothing
 * new is requested from the browser.
 *
 * Auto-open is honoured but not forced: `autoSurfacePlayer` is the
 * operator's stated intent, and the browser's autoplay policy still
 * decides whether sound follows.
 */

import React from 'react';
import { useSpatialProximity } from '../../hooks/useSpatialProximity';
import { spatialParticipantId } from '../../services/spatial/spatialVisitorId';
import SpatialBroadcastCard from './SpatialBroadcastCard';

export interface SpatialNearbyPanelProps {
  /** The listener's position, from the host app's own location state. */
  location?: { latitude: number; longitude: number; accuracy?: number } | null;
  wallet?: string | null;
  /** Numeric country code the player uses for its policy checks. */
  country?: number | null;
  /** Hidden entirely when false, so the map stays quiet on request. */
  enabled?: boolean;
  /**
   * An object already being listened to somewhere else on the map.
   *
   * Omitted from this panel so the same broadcast cannot be joined
   * twice at once. Two players in the same room means the utterance is
   * decoded and scheduled twice, which is audible as an echo — and
   * `autoSurfacePlayer` makes it happen without anybody pressing
   * anything.
   */
  excludeObjectId?: string | null;
  style?: React.CSSProperties;
}

/** More than this and the panel is a list, not a hint. */
const MAX_CARDS = 3;

export const SpatialNearbyPanel: React.FC<SpatialNearbyPanelProps> = ({
  location = null,
  wallet = null,
  country = null,
  enabled = true,
  excludeObjectId = null,
  style,
}) => {
  const position = location
    ? {
      lat: location.latitude,
      lng: location.longitude,
      accuracyMeters: location.accuracy ?? null,
    }
    : null;

  const { broadcasts } = useSpatialProximity({
    // The wallet when there is one, otherwise this browser's own
    // anonymous id. The tracker keys hysteresis per participant, so a
    // new id every poll would make every fix look like a fresh
    // arrival — and the shared literal this used to send made every
    // anonymous visitor on the map the same person.
    participantId: spatialParticipantId(wallet),
    position,
    wallet,
    enabled: enabled && !!position,
  });

  // Only what the listener is actually inside the radius of. A building
  // 900m away being live is true and not worth a card.
  const audible = broadcasts
    .filter((b) => b.withinEnterRadius && b.live)
    .filter((b) => b.spatialObjectId !== excludeObjectId)
    .slice(0, MAX_CARDS);

  if (audible.length === 0) return null;

  return (
    <div
      style={{
        position: 'absolute',
        bottom: 16,
        left: '50%',
        transform: 'translateX(-50%)',
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        zIndex: 5,
        pointerEvents: 'auto',
        ...style,
      }}
    >
      {audible.map((b) => (
        <SpatialBroadcastCard
          key={b.spatialObjectId}
          broadcast={b}
          viewerWallet={wallet}
          country={country}
          defaultOpen={b.autoSurfacePlayer}
        />
      ))}
    </div>
  );
};

export default SpatialNearbyPanel;
